import React from 'react';
import { Check } from 'lucide-react';

/*
 * StepTrack — the adoption loop, drawn as numbered stages.
 *
 * This is NOT the readiness scale (nascent → advanced, see the StageTrack in
 * pages/aai/dashboards/ui.tsx). That one measures how far a person has come;
 * this one says where the work in front of them sits in the loop: learn it,
 * practise it, use it on real work, confirm the outcome, sustain it.
 *
 * Done is ink and carries a tick, the current stage is Orbit yellow and
 * carries its number, and a stage not yet reached is an outlined circle. The
 * numbers stay visible on the stages ahead on purpose: a person should be able
 * to see how much loop is left, not just where they are.
 */

export interface TrackStep {
  key: string;
  label: string;
}

const STEP_MARK: Record<string, string> = {
  done: 'bg-ink text-white',
  current: 'bg-accent-500 text-ink',
  pending: 'border border-line-strong bg-white text-ink-soft',
};

const STEP_LABEL: Record<string, string> = {
  done: 'text-ink-muted',
  current: 'font-semibold text-ink',
  pending: 'text-ink-soft',
};

const stateOf = (index: number, current: number): string => {
  if (index < current) return 'done';
  if (index === current) return 'current';
  return 'pending';
};

export const StepTrack: React.FC<{
  steps: TrackStep[];
  /** Zero-based index of the stage the person is on. */
  current: number;
  className?: string;
}> = ({ steps, current, className = '' }) => (
  // Wraps rather than scrolls on a narrow column: a stage the reader cannot
  // see is a stage they do not know is coming. The connectors are the one
  // thing that goes, since a wrapped line joins the wrong pair.
  <ol className={`m-0 flex list-none flex-wrap items-center gap-x-2.5 gap-y-3 p-0 ${className}`}>
    {steps.map((step, i) => {
      const state = stateOf(i, current);
      const last = i === steps.length - 1;
      return (
        <li key={step.key} className="flex items-center gap-2.5 md:min-w-0 md:flex-1 md:last:flex-none">
          <span
            className={`grid h-7 w-7 shrink-0 place-items-center rounded-full font-sans text-[12px] font-semibold tabular-nums ${STEP_MARK[state]}`}
          >
            {state === 'done' && <Check size={14} strokeWidth={2.5} />}
            {state !== 'done' && i + 1}
          </span>
          <span className={`shrink-0 text-[13px] leading-[21.5px] ${STEP_LABEL[state]}`}>{step.label}</span>
          {!last && <span aria-hidden className="hidden h-px min-w-3 flex-1 bg-line md:block" />}
        </li>
      );
    })}
  </ol>
);
